/**
 * Service for managing removed anime IDs
 * Hybrid approach: stores locally in IndexedDB AND syncs to server when authenticated.
 * Server is the source of truth when available; IndexedDB is the fallback/cache.
 */
import databaseService from './sqliteService';
import { getUserItem, removeUserItem } from '../utils/userStorage';
import {
    getRemovedAnimeFromServer,
    addRemovedAnimeOnServer,
    removeRemovedAnimeOnServer,
    batchAddRemovedAnimeOnServer
} from './apiService';
import { waitForServerAuth } from './authReadyService';

const STORAGE_KEY = 'removedAnimeIds';
let migrationComplete = false;
let cachedIds = null; // In-memory cache for sync access

/**
 * Check if user is authenticated (has a token)
 */
const isAuthenticated = () => {
    const { hasToken } = require('./tokenStorage');
    return hasToken();
};

/**
 * Migrate removed anime IDs from localStorage to database (one-time)
 * @returns {Promise<void>}
 */
const migrateFromLocalStorage = async () => {
    if (migrationComplete) return;

    try {
        const stored = getUserItem(STORAGE_KEY);
        if (stored) {
            const ids = JSON.parse(stored);
            if (ids && ids.length > 0) {
                console.log(`📦 Migrating ${ids.length} removed anime from localStorage to database...`);
                await databaseService.batchAddRemovedAnime(ids);
                removeUserItem(STORAGE_KEY);
                console.log('✅ Migration complete, localStorage cleared');
            }
        }
        migrationComplete = true;
    } catch (error) {
        console.error('Error migrating removed anime from localStorage:', error);
        migrationComplete = true;
    }
};

/**
 * Initialize and get removed anime IDs (async, with migration)
 * Merges server + local IndexedDB data so nothing is lost.
 * Pushes any local-only IDs up to the server for permanent storage.
 * @returns {Promise<Set<number>>} Set of removed anime IDs
 */
export const initRemovedAnimeIds = async () => {
    await migrateFromLocalStorage();

    // Always load local IDs first
    const localIds = await databaseService.getRemovedAnimeIdsFromDb();

    if (isAuthenticated()) {
        try {
            await waitForServerAuth(); // Wait for server login to complete
            const serverIds = await getRemovedAnimeFromServer();
            const serverSet = new Set(serverIds || []);

            // Merge: union of server + local
            const merged = new Set([...localIds, ...serverSet]);

            // Find IDs that exist locally but not on server — push them up
            const localOnly = [...localIds].filter(id => !serverSet.has(id));
            if (localOnly.length > 0) {
                console.log(`📤 Syncing ${localOnly.length} local-only removed anime to server...`);
                batchAddRemovedAnimeOnServer(localOnly).catch(err =>
                    console.warn('⚠️ Failed to sync local removed anime to server:', err.message)
                );
            }

            // Find IDs that exist on server but not locally — save them locally
            const serverOnly = [...serverSet].filter(id => !localIds.has(id));
            if (serverOnly.length > 0) {
                await databaseService.batchAddRemovedAnime(serverOnly);
            }

            cachedIds = merged;
            console.log(`📡 Removed anime synced: ${merged.size} total (${localOnly.length} pushed to server, ${serverOnly.length} pulled from server)`);
            return cachedIds;
        } catch (error) {
            console.warn('⚠️ Could not load removed anime from server, using local:', error.message);
        }
    }

    // Fallback to local IndexedDB only
    cachedIds = localIds;
    return cachedIds;
};

/**
 * Cleanup stale removed anime IDs — removes entries that are now in the user's AniList.
 * Called after group building when we know the user's full anime list.
 * @param {Set<number>} userAnimeIds - Set of anime IDs currently in the user's list
 * @returns {Promise<number>} Number of stale entries cleaned up
 */
export const cleanupStaleRemovedAnime = async (userAnimeIds) => {
    if (!cachedIds || cachedIds.size === 0 || !userAnimeIds || userAnimeIds.size === 0) return 0;

    // Find removed IDs that are now in the user's list
    const staleIds = [...cachedIds].filter(id => userAnimeIds.has(id));

    if (staleIds.length === 0) return 0;

    console.log(`🧹 Cleaning up ${staleIds.length} stale removed anime (now in your list)...`);

    let cleaned = 0;
    for (const id of staleIds) {
        try {
            await databaseService.removeFromRemovedAnime(id);
            cachedIds.delete(id);
            cleaned++;

            // Also remove from server (fire and forget)
            if (isAuthenticated()) {
                removeRemovedAnimeOnServer(id).catch(() => { });
            }
        } catch (err) {
            console.warn(`Failed to clean up stale removed anime ${id}:`, err.message);
        }
    }

    if (cleaned > 0) {
        console.log(`✅ Cleaned up ${cleaned} stale removed anime IDs`);
        window.dispatchEvent(new Event('removedAnimeChanged'));
    }

    return cleaned;
};

/**
 * Get all removed anime IDs from cache (sync)
 * Falls back to localStorage if cache not initialized
 * @returns {Set<number>} Set of removed anime IDs
 */
export const getRemovedAnimeIds = () => {
    if (cachedIds !== null) {
        return new Set(cachedIds);
    }

    // Fallback to localStorage for initial render (before async init)
    try {
        const stored = getUserItem(STORAGE_KEY);
        if (!stored) return new Set();
        const ids = JSON.parse(stored);
        return new Set(ids);
    } catch (error) {
        console.error('Error reading removed anime:', error);
        return new Set();
    }
};

/**
 * Add an anime ID to the removed list
 * Saves locally AND to server
 * @param {number} animeId - The anime ID to remove
 * @returns {Promise<boolean>} Success status
 */
export const addRemovedAnime = async (animeId) => {
    try {
        // Save locally
        await databaseService.addRemovedAnimeToDb(animeId);
        if (cachedIds) {
            cachedIds = new Set([...cachedIds, animeId]);
        }

        // Notify listeners (nav badge count, etc.)
        window.dispatchEvent(new Event('removedAnimeChanged'));

        // Sync to server (fire and forget)
        if (isAuthenticated()) {
            addRemovedAnimeOnServer(animeId).catch(err =>
                console.warn('Server sync failed for removed anime:', err.message)
            );
        }

        return true;
    } catch (error) {
        console.error('Error adding removed anime:', error);
        return false;
    }
};

/**
 * Remove an anime ID from the removed list (restore it)
 * Restores locally AND on server
 * @param {number} animeId - The anime ID to restore
 * @returns {Promise<boolean>} Success status
 */
export const removeRemovedAnime = async (animeId) => {
    try {
        // Remove locally
        await databaseService.removeFromRemovedAnime(animeId);
        if (cachedIds) {
            cachedIds = new Set([...cachedIds].filter(id => id !== animeId));
        }

        // Notify listeners (nav badge count, etc.)
        window.dispatchEvent(new Event('removedAnimeChanged'));

        // Sync to server (fire and forget)
        if (isAuthenticated()) {
            removeRemovedAnimeOnServer(animeId).catch(err =>
                console.warn('Server sync failed for restoring anime:', err.message)
            );
        }

        return true;
    } catch (error) {
        console.error('Error removing anime from removed list:', error);
        return false;
    }
};

/**
 * Check if an anime is in the removed list
 * @param {number} animeId - The anime ID to check
 * @returns {boolean} True if anime is removed
 */
export const isAnimeRemoved = (animeId) => {
    const removedIds = getRemovedAnimeIds();
    return removedIds.has(animeId);
};

/**
 * Get count of removed anime
 * @returns {number} Number of removed anime
 */
export const getRemovedAnimeCount = () => {
    return getRemovedAnimeIds().size;
};

/**
 * Clear all removed anime (for debugging/reset purposes)
 * @returns {Promise<boolean>} Success status
 */
export const clearAllRemovedAnime = async () => {
    try {
        await databaseService.clearAllRemovedAnime();
        cachedIds = new Set();
        return true;
    } catch (error) {
        console.error('Error clearing removed anime:', error);
        return false;
    }
};
